<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="$t('Assign Clerks')" @show="onShow"
                   @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)" :show-confirmation="showConfirmation"
                   :ok-title="$t('Save')" :is-loading="loading" scrollable>

      <div class="row mb-2">
        <div class="col-md-12">
          <div class="d-flex justify-content-between">
            <input-widget :model="model" type="number" :label="false" attribute="pre_bids_lot_spacing"
                          :placeholder="$t('Lot Spacing')" :hint="$t('If left blank, default lot spacing will be 2')"/>
            <b-button style="height: fit-content" variant="primary" @click="onGenerateClick()">
              {{ $t('Generate') }}
            </b-button>
          </div>
          <div class="row">
            <div class="col-md-12">
              <b-alert class="mb-0 mt-3 login-alert-msg-box overflow-auto" variant="danger"
                       :show="errorMessages.length > 0"
                       dismissible style="max-height: 10em">
                <ul class="mb-0">
                  <li v-for="(msg, index) in errorMessages" :key="`error-message-${index}`">
                    {{ msg }}
                  </li>
                </ul>
              </b-alert>
            </div>
          </div>
          <div class="row">
            <div class="col-md-12">
              <hr class="mt-0"/>
            </div>
          </div>
          <div class="row">
            <div class="artisio-table assign-clerks-table col-md-12">
              <spinner :show="modalLoading" :withText="true"/>
              <div v-if="!modalLoading">
                <artisio-table :items="filteredBids" :enableFrontEndPagination="true" sticky-header
                               :pagination="pagination" :fields="tableFields" :enable-simple-search="false">

                  <template v-slot:search-form-placeholder>
                    <div class="row">
                      <div class="col-sm-10">
                        <div class="row">
                          <div class="col-md-3">
                            <input-widget :model="searchModel" attribute="lot_no" :label="false" :placeholder="true"/>
                          </div>
                          <div class="col-md-3">
                            <bidder-select-input-widget :model="searchModel" attribute="bidder_uuid" :label="false"
                                                        :options="{placeholder: $t('Bidder')}" value-field="uuid"
                                                        :auction-uuid="modal.object ? modal.object.uuid : null"
                                                        is-search @select="onBidderSelect"/>
                          </div>
                          <div class="col-md-3">
                            <clerk-select-input-widget :label="false" :model="searchModel"
                                                       attribute="preferred_clerk_uuid"
                                                       multiple
                                                       :auction="modal.object"
                                                       :options="{placeholder: $t('Pref. Clerk'), multiple: true}"/>
                          </div>
                          <div class="col-md-3">
                            <clerk-select-input-widget :label="false" :model="searchModel"
                                                       attribute="assigned_clerk_uuid"
                                                       multiple
                                                       :auction="modal.object"
                                                       :options="{placeholder: $t('Assigned Clerk'), multiple: true}"/>
                          </div>
                          <div class="col-md-3">
                            <currency-input-widget :model="searchModel" attribute="cover_amount_from" :label="false"
                                                   :placeholder="true"
                                                   type="number"/>
                          </div>
                          <div class="col-md-3">
                            <currency-input-widget :model="searchModel" attribute="cover_amount_to" :label="false"
                                                   :placeholder="true"
                                                   type="number"/>
                          </div>
                          <div class="col-md-3">
                            <language-select-input-widget :model="searchModel" attribute="preferred_language_uuid"
                                                          multiple
                                                          :label="false"
                                                          :options="{placeholder: $t('Pref. Language'), multiple: true}"/>
                          </div>
                          <div class="col-md-3 d-flex align-items-center">
                            <input-widget type="checkbox" :model="searchModel"
                                          attribute="show_unassigned_only"
                                          :label="$t('Show Unassigned Only')"/>
                          </div>
                        </div>
                      </div>
                      <div class="col-sm-2 text-right">
                        <b-button variant="primary" @click.prevent="onResetClick">
                          {{ $t('Reset') }}
                        </b-button>
                        <b-button type="submit" variant="primary" class="ml-2" @click.prevent="onFilterClick">
                          {{ $t('Search') }}
                        </b-button>
                      </div>
                    </div>
                  </template>

                  <template v-slot:head(lock)="tableFields">
                    <b-checkbox @change="selectAllRows($event)" v-model="isSelectedAll"
                                class="d-inline-block"/>
                    <span v-html="tableFields.label"></span>
                  </template>
                  <template slot="cell(lock)" slot-scope="{item: bid}">
                    <input-widget type="checkbox" :model="bid" attribute="clerk_assignment_lock" :label="false"
                                  :disabled="!bid.clerk_uuid" @change="onCheckboxChange($event)"/>
                  </template>
                  <template slot="cell(lot)" slot-scope="{item: bid}">
                    <router-link v-if="$hasPermission('view_inventoryitem') && bid.lot"
                                 :to="{ name: 'inventory.view', params: { id: bid.lot.inventory_uuid }}">
                      {{ bid.lot.lot_no }}
                    </router-link>
                  </template>
                  <template slot="cell(bidder)" slot-scope="{item: bid}">
                    <router-link v-if="$hasPermission('view_customer')"
                                 :to="{ name: 'customers.dashboard', params: { id: bid.bidder.customer ? bid.bidder.customer.uuid :
                      ''}}">
                      {{ bid.bidder.customer ? bid.bidder.customer.display_name : '' }}
                    </router-link>
                    <p v-else> {{ bid.bidder.customer ? bid.bidder.customer.display_name : '' }} </p>
                  </template>
                  <template slot="cell(pref_clerk)" slot-scope="{item: bid}">
                    {{ bid.bidder.preferred_clerk ? bid.bidder.preferred_clerk.name : '' }}
                  </template>
                  <template slot="cell(pref_language)" slot-scope="{item: bid}">
                    {{ bid.bidder.language ? bid.bidder.language.name : '' }}
                  </template>
                  <template slot="cell(cover_amount)" slot-scope="{item: bid}">
                    <div v-if="modal.object.currency">
                      {{ bid.amount | asCurrency(modal.object.currency.symbol) }}
                    </div>
                  </template>
                  <template slot="cell(assigned_clerk)" slot-scope="{item: bid}">
                    <clerk-select-input-widget :label="false" :model="bid" attribute="clerk_uuid" type="text"
                                               :auction="modal.object" @select="onClerkChange(bid)"
                                               @unselect="onClerkChange(bid)"/>
                  </template>

                </artisio-table>

              </div>
            </div>
          </div>
        </div>
      </div>
    </artisio-modal>
    <clerks-modal/>
  </ValidationObserver>
</template>

<script>

  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import AuctionClerk from "@/modules/pre-bids/models/AuctionClerk.model";
  import ClerksModal from "@/modules/setup/branch/tabs/clerks/ClerksModal.vue";
  import ClerkSelectInputWidget from "@/core/widgets/ClerkSelectInputWidget.vue";
  import AssignClerks from "@/modules/pre-bids/models/AssignClerks.model";
  import PreBidsModal from "@/modules/pre-bids/models/PreBidsModal.model";
  import Spinner from "@/core/components/common/Spinner.vue";
  import PreBidsService from "@/modules/pre-bids/PreBidsService";
  import AssignClerksSearchModel from "@/modules/pre-bids/models/AssignClerksSearch.model";
  import BidderSelectInputWidget from "@/core/widgets/BidderSelectInputWidget";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import LanguageSelectInputWidget from "@/core/widgets/LanguageSelectInputWidget";
  import ArtisioTable from "@/core/components/ams-table/ArtisioTable";
  import i18n from "@/shared/i18n";
  import {cloneDeep} from "lodash";

  const {mapState, mapActions} = createNamespacedHelpers('preBids');
  const {mapActions: clerksMapActions} = createNamespacedHelpers('setup/branch');

  export default {
    name: "AssignClerksModal",
    components: {
      ArtisioTable,
      LanguageSelectInputWidget,
      CurrencyInputWidget,
      BidderSelectInputWidget,
      Spinner,
      ClerkSelectInputWidget,
      ClerksModal,
      InputWidget,
      ArtisioModal,
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new AssignClerks(),
        searchModel: new AssignClerksSearchModel(),
        clerks: [],
        filteredBids: [],
        allBids: [],
        errorMessages: [],
        isSelectedAll: false,
        tableFields: [
          {key: 'lock', label: i18n.t('Lock')},
          {key: 'lot', label: i18n.t('Lot')},
          {key: 'bidder', label: i18n.t('Bidder')},
          {key: 'pref_clerk', label: i18n.t('Pref. Clerk')},
          {key: 'pref_language', label: i18n.t('Pref. Language')},
          {key: 'cover_amount', label: i18n.t('Cover Amount')},
          {key: 'assigned_clerk', label: i18n.t('Assigned Clerk')},
        ],
        pagination: {
          limit: 50,
          page: 1,
          total: 1,
        }
      }
    },
    computed: {
      ...mapState({
        modal: state => state.assignClerksModal,
        preBids: state => state.assignClerksModal.preBids,
        modalLoading: state => state.assignClerksModal.loading,
      }),
    },
    watch: {
      preBids() {
        if (this.preBids) {
          this.filteredBids = this.preBids.map(bid => new PreBidsModal(bid))
          this.allBids = cloneDeep(this.preBids.map(bid => new PreBidsModal(bid)))
          this.calculateIsSelectedAll()
          this.pagination.total = this.filteredBids.length
        }
      },
      pagination: {
        deep: true,
        handler() {
          this.calculateIsSelectedAll();
        }
      }
    },
    methods: {
      ...mapActions(['hideAssignClerksModal', 'saveAssignedClerks', 'updateAssignClerksModalLoading']),
      ...clerksMapActions({
        showClerksModal: 'showClerksModal'
      }),
      onShow() {
        this.showConfirmation = true;
        this.selectedClerks = this.modal.object.clerks;
        this.clerks = this.modal.object.clerks;
        this.model.telephone_bids_lot_spacing = this.modal.object.telephone_bids_lot_spacing;
      },
      onHidden() {
        this.model = new AuctionClerk();
        this.searchModel = new AssignClerksSearchModel()
        this.filteredBids = []
        this.pagination.page = 1
        this.selectedClerks = [];
        this.hideAssignClerksModal();
        this.errorMessages = []
      },
      async onSubmit() {
        let requestData = [];

        let bidsWithoutClerks = this.filteredBids.filter(bid => !bid.clerk_uuid);

        if (bidsWithoutClerks.length > 0) {
          let bidderToLotsMap = {}
          bidsWithoutClerks.forEach(bid => {
            if (!bidderToLotsMap[bid.bidder.uuid]) {
              bidderToLotsMap[bid.bidder.uuid] = {
                'display_name': bid.bidder.customer.display_name,
                'lot_nos': []
              }
            }
            bidderToLotsMap[bid.bidder.uuid]['lot_nos'].push(bid.lot_no)
          })

          let bidderMessages = []
          for (let bidderUuid in bidderToLotsMap) {
            bidderMessages.push(this.$i18n.t("{display_name} - Lot(s): {lots}", {
              display_name: bidderToLotsMap[bidderUuid]['display_name'],
              lots: bidderToLotsMap[bidderUuid]['lot_nos'].join(','),
            }))
          }

          let messages = []
          messages.push(this.$i18n.t('No clerks are assigned for the following bidders: '))
          messages.push('<br>')
          messages.push(bidderMessages.join('<br>'))
          messages.push('<br>')
          messages.push(this.$i18n.t('Do you want to continue?'))

          let success = await this.$confirm(messages.join(' '))
          if (!success) {
            return false
          }
        }


        this.filteredBids.forEach((bid) => {
          requestData.push({
            uuid: bid.uuid,
            clerk_uuid: bid.clerk_uuid,
            clerk_assignment_lock: !!(bid.clerk_assignment_lock && bid.clerk_uuid)
          })
        });

        let saveResponse = await this.saveAssignedClerks({
          sale_uuid: this.modal.object.uuid,
          pre_bids_lot_spacing: this.model.pre_bids_lot_spacing,
          requestData
        });

        if (saveResponse.success) {
          this.showConfirmation = false;
          this.hideAssignClerksModal();
          this.$toast('success', this.$t('Clerks successfully assigned'));
        } else {
          if (saveResponse.toastError) {
            this.$errorDialog(saveResponse.toastError);
          } else {
            if (saveResponse.body.non_field_errors) {
              this.$errorDialog(saveResponse.body.non_field_errors[0]);
            } else {
              for (let index in saveResponse.body) {
                this.filteredBids[index].setMultipleErrors(saveResponse.body[index]);
              }
            }
          }
        }

      },
      async onGenerateClick() {
        if (this.filteredBids.length !== this.allBids.length) {
          this.$errorDialog("Please, remove filters and try generation again. To remove all filter in one click, please, click reset button.")
          return
        }

        await this.onResetClick(false)

        if (!this.model.pre_bids_lot_spacing) {
          this.model.pre_bids_lot_spacing = 2
        }
        this.errorMessages = []
        this.model.resetErrors();
        await this.updateAssignClerksModalLoading(true)
        let response = await PreBidsService.generateAssignClerks({
          sale_no: this.modal.object.sale_no,
          pre_bids_lot_spacing: this.model.pre_bids_lot_spacing,
          locked_pre_bids_info: this.filteredBids.filter(b => b.clerk_assignment_lock && b.clerk_uuid).map(bid => {
            return {
              'pre_bid_uuid': bid.uuid,
              'clerk_uuid': bid.clerk_uuid
            }
          })
        });
        if (response.success) {
          this.filteredBids = response.body.map(bid => new PreBidsModal(bid))
          this.allBids = cloneDeep(response.body.map(bid => new PreBidsModal(bid)))

          for (let item of this.filteredBids) {
            if (!item.clerk_uuid && item.bidder && item.bidder.customer && item.bidder.customer.display_name) {
              this.errorMessages.push(this.$i18n.t('{bidder} - {lot_no} - Unable to assign clerk', {
                'bidder': item.bidder.customer.display_name,
                'lot_no': item.lot_no
              }))
            } else if (
              item.clerk && item.bidder &&
              item.bidder.language_uuid &&
              item.clerk.language_uuids &&
              item.clerk.language_uuids.length > 0 &&
              !item.clerk.language_uuids.includes(item.bidder.language_uuid)
            ) {
              this.errorMessages.push(this.$i18n.t("{bidder} - {lot_no} - The languages used by the bidder and the clerk do not correspond.", {
                'bidder': item.bidder.customer.display_name,
                'lot_no': item.lot_no
              }))
            }
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0])
          } else {
            this.model.setMultipleErrors(response.body);
          }
        }
        await this.updateAssignClerksModalLoading(false)
      },
      async onResetClick(updateLoaderBack = true) {
        await this.updateAssignClerksModalLoading(true)
        this.pagination.page = 1
        this.searchModel = new AssignClerksSearchModel()
        this.filteredBids = cloneDeep(this.allBids)
        this.pagination.total = this.filteredBids.length

        this.calculateIsSelectedAll()

        setTimeout(() => {
          if (updateLoaderBack) {
            this.updateAssignClerksModalLoading(false)
          }
        }, 500)
        return true
      },
      async onFilterClick() {
        await this.updateAssignClerksModalLoading(true)

        let lot_no = this.searchModel.lot_no
        let bidder_uuid = this.searchModel.bidder_uuid
        let preferred_clerk_uuid = this.searchModel.preferred_clerk_uuid
        let preferred_language_uuid = this.searchModel.preferred_language_uuid
        let cover_amount_from = this.searchModel.cover_amount_from ? parseFloat(this.searchModel.cover_amount_from) : null
        let cover_amount_to = this.searchModel.cover_amount_to ? parseFloat(this.searchModel.cover_amount_to) : null
        let assigned_clerk_uuid = this.searchModel.assigned_clerk_uuid
        let show_unassigned_only = this.searchModel.show_unassigned_only

        this.filteredBids = this.allBids.filter(b => {
          if (lot_no && `${b.lot.lot_no}` !== lot_no) return false;

          if (bidder_uuid && b.bidder.uuid !== bidder_uuid) return false;

          if (
            preferred_language_uuid &&
            preferred_language_uuid.length > 0 &&
            !preferred_language_uuid.includes(b.bidder.language_uuid)
          ) return false;

          if (
            preferred_clerk_uuid &&
            preferred_clerk_uuid.length > 0 &&
            !preferred_clerk_uuid.includes(b.bidder.preferred_clerk_uuid)
          ) return false;

          if (
            assigned_clerk_uuid &&
            assigned_clerk_uuid.length > 0 &&
            !assigned_clerk_uuid.includes(b.clerk_uuid)
          ) return false;

          if (show_unassigned_only && b.clerk_uuid) return false;

          if (typeof cover_amount_from === 'number' && b.amount < cover_amount_from)
            return false;

          if (typeof cover_amount_to === 'number' && b.amount > cover_amount_to)
            return false;

          return true;
        })
        this.pagination.page = 1
        this.pagination.total = this.filteredBids.length

        this.calculateIsSelectedAll()

        setTimeout(() => {
          this.updateAssignClerksModalLoading(false)
        }, 500)
      },
      calculateIsSelectedAll() {
        let lotsWithLockTurnedOff = 0
        let lotsWithClerksSelected = 0

        let startIndex;
        let endIndex;
        if (this.pagination.page === 1) {
          startIndex = 0
          endIndex = this.pagination.limit - 1
        } else {
          startIndex = ((this.pagination.page - 1) * this.pagination.limit)
          endIndex = (this.pagination.page * this.pagination.limit) - 1
        }

        for (let i = startIndex; i <= endIndex; i++) {
          if (!this.filteredBids[i]) {
            break
          }
          if (!this.filteredBids[i].clerk_uuid) {
            continue
          }
          lotsWithClerksSelected++
          if (!this.filteredBids[i].clerk_assignment_lock) {
            lotsWithLockTurnedOff++
          }
        }

        if (lotsWithClerksSelected > 0) {
          this.isSelectedAll = lotsWithLockTurnedOff === 0
        } else {
          this.isSelectedAll = false
        }
      },
      selectAllRows(checked) {
        this.isSelectedAll = checked;
        this.filteredBids.forEach(bid => {
          bid.clerk_assignment_lock = checked
        })
      },
      onCheckboxChange() {
        this.$nextTick(() => {
          this.calculateIsSelectedAll()
        })
      },
      onClerkChange(bid) {
        this.$nextTick(() => {
          let bidFromAllBids = this.allBids.find(b => b.uuid === bid.uuid)
          bidFromAllBids.clerk_uuid = bid.clerk_uuid
        })
      },
      onBidderSelect(bidder) {
        this.searchModel.bidder = bidder
      }
    }
  }
</script>

<style scoped>
  /deep/ .clerks-table .form-group {
    margin-bottom: 0rem;
  }

  .assign-clerks-table .table th, .table td {
    padding: 0.25rem
  }

  /deep/ .artisio-table {
    overflow: hidden;
  }

  /deep/ .artisio-table .table-content {
    display: flex;
    overflow: auto;
  }

  /deep/ .artisio-table .table-content .table-wrapper {
    max-height: 30em !important;
  }

</style>
